// /inventory/thresholds · low-stock threshold grid (Phase 2b, polished).
// Rows = products, columns = RH / DT / NY / CK. Each cell holds the threshold
// for that (product, location), or blank for "no alert". Edit inline + save
// on blur; clearing the value deletes the threshold (admin gets a push when
// any cell drops below threshold — see worker/src/routes/inventory.ts).
//
// Polished:
//   - Sticky toolbar with shift filter + search + configured-count meta
//   - inv-grid surface (zebra, hover, tabular figures)
//   - displayName(p) strips redundant prefix
//   - Inputs use design-system tokens, not inline styles

import { useState, useEffect, useMemo } from 'react';
import * as api from '../lib/api.ts';
import { displayName } from '../lib/products.ts';
import { LOCATIONS as LOCS, locationLabel } from '../lib/locations.ts';

const SHIFT_OPTIONS = [
  { key: 'all',   label: '全部' },
  { key: 'day',   label: '☀ 白班' },
  { key: 'night', label: '🌙 晚班' },
  { key: 'both',  label: '通用' }
];

export const InventoryThresholds = () => {
  const [products, setProducts] = useState(null);
  const [thresholds, setThresholds] = useState([]);
  const [err, setErr] = useState('');
  const [shift, setShift] = useState('all');
  const [search, setSearch] = useState('');

  async function refresh() {
    try {
      const [p, t] = await Promise.all([api.listProducts(), api.listThresholds()]);
      setProducts(p);
      setThresholds(t);
      setErr('');
    } catch (e) { setErr(e?.message || '加载失败'); }
  }
  useEffect(() => { refresh(); }, []);

  const map = useMemo(
    () => new Map(thresholds.map(t => [`${t.productId}|${t.location}`, t.threshold])),
    [thresholds]
  );

  const visibleRows = useMemo(() => {
    if (!products) return [];
    let rows = products;
    if (shift !== 'all') rows = rows.filter(p => p.shift === shift || p.shift === 'both');
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q) ||
        (p.variant || '').toLowerCase().includes(q)
      );
    }
    return [...rows].sort((a, b) =>
      (a.category || '').localeCompare(b.category || '', 'zh')
        || a.name.localeCompare(b.name, 'zh')
    );
  }, [products, shift, search]);

  if (products === null && !err) return <div className="vava-card empty-state">加载中…</div>;

  const configuredCount = thresholds.length;

  return (
    <>
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">低库存阈值</h1>
          <div className="vava-page-meta">
            为每个 (产品 × 门店) 设置阈值。库存低于阈值时推送提醒到管理员。
            {configuredCount > 0 && ` · 已配置 ${configuredCount} 个`}
          </div>
        </div>
      </div>

      <div className="inv-toolbar">
        <div className="inv-segmented" role="tablist" aria-label="班次筛选">
          {SHIFT_OPTIONS.map(o => (
            <button key={o.key}
                    role="tab"
                    aria-selected={shift === o.key}
                    onClick={() => setShift(o.key)}>
              {o.label}
            </button>
          ))}
        </div>
        <div className="inv-search">
          <input type="text"
                 value={search}
                 onChange={(e) => setSearch(e.target.value)}
                 placeholder="搜 产品 / 品类 / 口味…" />
        </div>
        <span className="vava-hint" style={{ marginLeft: 'auto' }}>
          {visibleRows.length} / {(products ?? []).length}
        </span>
      </div>

      {err && <div className="att-error inv-error" role="alert">{err}</div>}

      {visibleRows.length === 0 ? (
        <div className="inv-grid-wrap">
          <div className="inv-empty">
            <div className="emoji">⚠️</div>
            <div className="title">
              {(products ?? []).length === 0 ? '还没有产品' : '没有匹配的产品'}
            </div>
            <div className="sub">
              {(products ?? []).length === 0
                ? '先在「管理产品」里添加 SKU'
                : '调整筛选或清空搜索'}
            </div>
          </div>
        </div>
      ) : (
        <div className="inv-grid-wrap">
          <table className="inv-grid">
            <colgroup>
              <col style={{ width: '20%' }} />
              <col style={{ width: '30%' }} />
              {LOCS.map(l => <col key={l} style={{ width: '12.5%' }} />)}
            </colgroup>
            <thead>
              <tr>
                <th>品类</th>
                <th>产品</th>
                {LOCS.map(l => <th key={l} className="num">{locationLabel(l)}</th>)}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(p => (
                <tr key={p.id}>
                  <td className="inv-name-sub">{p.category || '—'}</td>
                  <td className="inv-name">{displayName(p)}</td>
                  {LOCS.map(loc => (
                    <td key={loc} className="inv-thresh-cell">
                      <ThresholdInput
                        productId={p.id} location={loc}
                        initial={map.get(`${p.id}|${loc}`)}
                        onSaved={refresh} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="inv-footnote">
        留空 = 不为该 (产品, 门店) 设置阈值 · 输入数字 + Tab / Enter 保存 · 清空已设值会删除该阈值
      </div>
    </>
  );
};

const ThresholdInput = ({ productId, location, initial, onSaved }) => {
  const [v, setV] = useState(initial === undefined ? '' : String(initial));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { setV(initial === undefined ? '' : String(initial)); }, [initial]);

  async function save() {
    const trimmed = v.trim();
    setErr('');
    if (trimmed === '' && initial !== undefined) {
      setSaving(true);
      try { await api.clearThreshold(productId, location); await onSaved(); }
      catch (e) { setErr(e?.message || '保存失败'); setV(String(initial)); }
      finally { setSaving(false); }
      return;
    }
    if (trimmed === '') return;
    const n = parseInt(trimmed, 10);
    if (isNaN(n) || n < 0) {
      setV(initial === undefined ? '' : String(initial));
      return;
    }
    if (n === initial) return;
    setSaving(true);
    try { await api.setThreshold(productId, location, n); await onSaved(); }
    catch (e) { setErr(e?.message || '保存失败'); setV(initial === undefined ? '' : String(initial)); }
    finally { setSaving(false); }
  }

  const isSet = initial !== undefined;
  return (
    <div className="inv-thresh-input-wrap">
      <input type="number" min="0" placeholder="—"
             value={v} disabled={saving}
             onChange={(e) => setV(e.target.value)}
             onBlur={save}
             onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
             className={`inv-thresh-input ${isSet ? 'is-set' : ''}`} />
      {err && <div className="inv-thresh-err">{err}</div>}
    </div>
  );
};
